import { createFileRoute } from '@tanstack/react-router'
import { FabricSourcesPage, type FabricSourcesParams } from '@bloo-team/product-fabric'

/**
 * Composer shim (S5.0, docs/modularization.md §Route mounting). `?extractor=`
 * is the context-carrying pivot from Datafabric · Extractors (IA6): the page
 * narrows to devices running that extractor. `?status=` is the pivot from
 * Datafabric · Overview: a pipeline-health state (`sourceState`), or
 * `archived` — the per-browser archive view (device cleanup); anything
 * else is dropped.
 *
 * `?tab=connectors` + `?health=<verdict>` — the Connectors tab (tenant admins).
 */
function SourcesRoute() {
  const { clusterId, scopeId } = Route.useParams()
  const { extractor, status, tab, health } = Route.useSearch()
  return <FabricSourcesPage clusterId={clusterId} scopeId={scopeId} extractor={extractor} status={status} tab={tab} health={health} />
}

const STATUSES = ['active', 'stale', 'inactive', 'decommissioned', 'archived'] as const satisfies readonly NonNullable<FabricSourcesParams['status']>[]
const HEALTH = ['healthy', 'starting', 'unverified', 'failing'] as const satisfies readonly NonNullable<FabricSourcesParams['health']>[]
const isStatus = (v: unknown): v is NonNullable<FabricSourcesParams['status']> =>
  typeof v === 'string' && (STATUSES as readonly string[]).includes(v)
const isHealth = (v: unknown): v is NonNullable<FabricSourcesParams['health']> =>
  typeof v === 'string' && (HEALTH as readonly string[]).includes(v)

export const Route = createFileRoute('/$clusterId/$scopeId/fabric/sources')({
  validateSearch: (search: Record<string, unknown>): FabricSourcesParams => ({
    extractor:
      typeof search.extractor === 'string' && search.extractor.trim() ? search.extractor.trim() : undefined,
    status: isStatus(search.status) ? search.status : undefined,
    tab: search.tab === 'connectors' ? 'connectors' : undefined,
    health: isHealth(search.health) ? search.health : undefined,
  }),
  component: SourcesRoute,
})
